import { DailyBudgetType } from '../../../../../__generated__/graphql'
import { BudgetHistoryChartPoint } from './budgetHistoryChartSeries'
import { budgetHistoryChartConf } from './budgetHistoryChartConf'

export const BUDGET_DISPLAY_DOMAIN: [number, number] = [0, 1]

const LOWER_DISPLAY_TOP = 0.42
const UPPER_DISPLAY_BOTTOM = 0.58

export type BudgetAxisMode = 'broken' | 'log'

export type BrokenBudgetAxisConfig = {
  lowerMax: number
  upperMin: number
  upperMax: number
}

export const BUDGET_SERIES_VALUE_KEYS: Array<
  keyof Pick<
    BudgetHistoryChartPoint,
    | 'dailyBudget_Open'
    | 'dailyBudget_Fixed'
    | 'dailyBudget_Auto'
    | 'budgetCapping_Open'
    | 'budgetCapping_Monthly'
    | 'budgetCapping_Period'
    | 'budgetCapping_Overall'
  >
> = [
  'dailyBudget_Open',
  'dailyBudget_Fixed',
  'dailyBudget_Auto',
  'budgetCapping_Open',
  'budgetCapping_Monthly',
  'budgetCapping_Period',
  'budgetCapping_Overall',
]

const maxDailyMagnitude = (points: BudgetHistoryChartPoint[]): number =>
  points.reduce((max, point) => {
    const fixedAmount = point.dailyBudgetType === DailyBudgetType.Fixed ? point.dailyBudgetAmount : null
    const candidates = [fixedAmount, point.dailyBudget_Fixed].filter(
      (value): value is number => value != null && Number.isFinite(value) && value > 0
    )
    return Math.max(max, ...candidates, 0)
  }, 0)

const maxCappingMagnitude = (points: BudgetHistoryChartPoint[]): number =>
  points.reduce((max, point) => {
    const candidates = [
      point.budgetCappingAmount,
      point.budgetCapping_Monthly,
      point.budgetCapping_Period,
      point.budgetCapping_Overall,
    ].filter((value): value is number => value != null && Number.isFinite(value) && value > 0)
    return Math.max(max, ...candidates, 0)
  }, 0)

const minCappingMagnitude = (points: BudgetHistoryChartPoint[]): number | undefined => {
  let min: number | undefined
  points.forEach((point) => {
    const candidates = [
      point.budgetCappingAmount,
      point.budgetCapping_Monthly,
      point.budgetCapping_Period,
      point.budgetCapping_Overall,
    ].filter((value): value is number => value != null && value > 0)
    candidates.forEach((value) => {
      min = min === undefined ? value : Math.min(min, value)
    })
  })
  return min
}

const maxBudgetMagnitude = (points: BudgetHistoryChartPoint[]): number =>
  points.reduce((max, point) => {
    BUDGET_SERIES_VALUE_KEYS.forEach((key) => {
      const value = point[key]
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        max = Math.max(max, value)
      }
    })
    return max
  }, 0)

const minPositiveBudgetMagnitude = (points: BudgetHistoryChartPoint[]): number | undefined => {
  let min: number | undefined
  points.forEach((point) => {
    BUDGET_SERIES_VALUE_KEYS.forEach((key) => {
      const value = point[key]
      if (typeof value === 'number' && value > 0) {
        min = min === undefined ? value : Math.min(min, value)
      }
    })
  })
  return min
}

export const getBrokenBudgetAxisConfig = (points: BudgetHistoryChartPoint[]): BrokenBudgetAxisConfig | undefined => {
  const graphMax = maxBudgetMagnitude(points)
  if (graphMax <= budgetHistoryChartConf.brokenAxisWhenMaxBudgetAbove) {
    return undefined
  }

  const dailyMax = maxDailyMagnitude(points)
  const cappingMax = maxCappingMagnitude(points)
  const cappingMin = minCappingMagnitude(points)

  const lowerSourceMax = dailyMax > 0 ? dailyMax : cappingMax
  const upperSourceMax = cappingMax > 0 ? cappingMax : dailyMax
  if (lowerSourceMax <= 0 || upperSourceMax <= 0) return undefined

  const lowerMax = lowerSourceMax * 1.05
  const upperMax = upperSourceMax * 1.05
  const upperMin =
    cappingMax > 0
      ? Math.min(cappingMin ?? cappingMax, cappingMax * 0.95)
      : Math.max(lowerMax * 1.01, upperSourceMax * 0.95)

  if (upperMin <= lowerMax) {
    return {
      lowerMax,
      upperMin: lowerMax * 1.01,
      upperMax,
    }
  }

  return { lowerMax, upperMin, upperMax }
}

export const getBudgetAxisMode = (
  points: BudgetHistoryChartPoint[],
  showDailyBudget: boolean,
  showBudgetCapping: boolean
): BudgetAxisMode => {
  if (showDailyBudget && showBudgetCapping && getBrokenBudgetAxisConfig(points) != null) {
    return 'broken'
  }

  return 'log'
}

export const getBudgetLogAxisDomain = (points: BudgetHistoryChartPoint[]): [number, 'auto'] => {
  const minPositive = minPositiveBudgetMagnitude(points)
  return [minPositive ?? 1, 'auto']
}

export const valueToBrokenBudgetDisplay = (value: number, config: BrokenBudgetAxisConfig): number | null => {
  if (!Number.isFinite(value)) return null
  if (value <= config.lowerMax) {
    if (config.lowerMax <= 0) return 0
    return (value / config.lowerMax) * LOWER_DISPLAY_TOP
  }
  if (value >= config.upperMin) {
    const span = config.upperMax - config.upperMin
    if (span <= 0) return UPPER_DISPLAY_BOTTOM
    const t = (value - config.upperMin) / span
    return UPPER_DISPLAY_BOTTOM + t * (1 - UPPER_DISPLAY_BOTTOM)
  }
  return null
}

export const brokenBudgetDisplayToValue = (display: number, config: BrokenBudgetAxisConfig): number | null => {
  if (display <= LOWER_DISPLAY_TOP) {
    return (display / LOWER_DISPLAY_TOP) * config.lowerMax
  }
  if (display >= UPPER_DISPLAY_BOTTOM) {
    const span = 1 - UPPER_DISPLAY_BOTTOM
    if (span <= 0) return config.upperMax
    const t = (display - UPPER_DISPLAY_BOTTOM) / span
    return config.upperMin + t * (config.upperMax - config.upperMin)
  }
  return null
}

/** Display-scale tick in the gap (no currency label); used for the break symbol. */
export const BROKEN_BUDGET_AXIS_BREAK_TICK = (LOWER_DISPLAY_TOP + UPPER_DISPLAY_BOTTOM) / 2

export const BROKEN_BUDGET_AXIS_TICKS: number[] = [
  0,
  LOWER_DISPLAY_TOP,
  BROKEN_BUDGET_AXIS_BREAK_TICK,
  UPPER_DISPLAY_BOTTOM,
  1,
]

export const formatBrokenBudgetAxisTick = (
  display: number,
  config: BrokenBudgetAxisConfig,
  formatCurrency: (value: number) => string
): string => {
  if (display === 0) return ''
  if (Math.abs(display - BROKEN_BUDGET_AXIS_BREAK_TICK) < 0.001) return ''
  if (Math.abs(display - LOWER_DISPLAY_TOP) < 0.001) return formatCurrency(config.lowerMax)
  if (Math.abs(display - UPPER_DISPLAY_BOTTOM) < 0.001) return formatCurrency(config.upperMin)
  if (Math.abs(display - 1) < 0.001) return formatCurrency(config.upperMax)
  const value = brokenBudgetDisplayToValue(display, config)
  return value != null ? formatCurrency(value) : ''
}

export const applyBrokenBudgetAxisToPoint = (
  point: BudgetHistoryChartPoint,
  config: BrokenBudgetAxisConfig
): BudgetHistoryChartPoint => {
  const next: BudgetHistoryChartPoint = { ...point }

  BUDGET_SERIES_VALUE_KEYS.forEach((key) => {
    const value = point[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      next[key] = valueToBrokenBudgetDisplay(value, config)
    }
  })

  return next
}

export const BUDGET_AXIS_BREAK_DISPLAY = {
  lowerTop: LOWER_DISPLAY_TOP,
  upperBottom: UPPER_DISPLAY_BOTTOM,
} as const
